import React from 'react';
import { TARGET_STATUS } from './constants';

/**
 * DevelopmentTargetBar — reusable, PURE presentation of a backend benchmark
 * comparison (services/benchmark_engine.evaluate_metric output). It must NOT
 * select benchmarks, compute applicability, invent thresholds or reinterpret
 * directionality — it only renders what the backend returned.
 *
 * Props:
 *   comparison: the evaluate_metric dict
 *     { status, status_label, measured_value, unit, distance_to_target,
 *       message, benchmark: { representation_type, directionality,
 *       range:{low,high}, target_value, categorical_value, unit } | null }
 *   trend: optional { label, direction } shown SEPARATELY from status (§31).
 */
const TONE = {
  positive: { bar: 'bg-emerald-500', chip: 'bg-emerald-50 text-emerald-700 border-emerald-200', dot: 'bg-emerald-500' },
  progress: { bar: 'bg-sky-500', chip: 'bg-sky-50 text-sky-700 border-sky-200', dot: 'bg-sky-500' },
  priority: { bar: 'bg-amber-500', chip: 'bg-amber-50 text-amber-800 border-amber-200', dot: 'bg-amber-500' },
  neutral: { bar: 'bg-slate-400', chip: 'bg-slate-100 text-slate-600 border-slate-200', dot: 'bg-slate-400' },
};

function clampPct(n) { return Math.max(4, Math.min(96, n)); }

const DevelopmentTargetBar = ({ comparison, trend = null, label = null, 'data-testid': testId = 'dev-target-bar' }) => {
  if (!comparison) return null;
  const status = comparison.status || 'insufficient_data';
  const meta = TARGET_STATUS[status] || TARGET_STATUS.insufficient_data;
  const tone = TONE[meta.tone] || TONE.neutral;
  const unit = comparison.unit || comparison.benchmark?.unit || '';
  const measured = comparison.measured_value;

  // Insufficient data — professional empty state, never an empty chart (§26).
  if (status === 'insufficient_data') {
    return (
      <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5" data-testid={testId}>
        {label && <div className="text-xs font-medium text-slate-500 mb-0.5">{label}</div>}
        <div className="text-sm text-slate-500">
          {comparison.message || 'No validated development target available for this metric.'}
        </div>
      </div>
    );
  }

  const bench = comparison.benchmark || {};
  const rep = bench.representation_type;
  const dir = bench.directionality;
  const low = bench.range?.low;
  const high = bench.range?.high;
  const target = bench.target_value;

  // Compute a display scale so the current marker + zone are readable. Uses
  // only backend values — no threshold invention.
  const nums = [measured, low, high, target].filter((x) => typeof x === 'number');
  const min = Math.min(...nums);
  const max = Math.max(...nums);
  const span = max - min || Math.abs(max) || 1;
  const pad = span * 0.25;
  const lo = min - pad;
  const hi = max + pad;
  const scale = (v) => clampPct(((v - lo) / (hi - lo)) * 100);

  const markerPct = typeof measured === 'number' ? scale(measured) : null;
  let zoneLeft = null; let zoneWidth = null;
  if (rep === 'range' && typeof low === 'number' && typeof high === 'number') {
    zoneLeft = scale(low); zoneWidth = Math.max(4, scale(high) - scale(low));
  } else if (dir === 'higher_better' && typeof target === 'number') {
    zoneLeft = scale(target); zoneWidth = clampPct(100 - scale(target));
  } else if (dir === 'lower_better' && typeof target === 'number') {
    zoneLeft = 0; zoneWidth = scale(target);
  } else if (typeof target === 'number') {
    zoneLeft = Math.max(0, scale(target) - 6); zoneWidth = 12;
  }

  const contextOnly = status === 'context_only' || dir === 'context_only';

  return (
    <div className="rounded-lg border border-slate-200 bg-white px-3 py-3" data-testid={testId}>
      {label && <div className="text-xs font-medium text-slate-500 mb-1.5">{label}</div>}

      <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
        <div className="text-lg font-semibold text-slate-800" data-testid={`${testId}-value`}>
          {typeof measured === 'number' ? measured : '—'}
          {unit ? <span className="text-sm font-normal text-slate-400 ml-1">{unit}</span> : null}
        </div>
        {!contextOnly && (
          <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium ${tone.chip}`} data-testid={`${testId}-status`}>
            <span className={`h-1.5 w-1.5 rounded-full ${tone.dot}`} />
            {comparison.status_label || meta.label}
          </span>
        )}
        {contextOnly && (
          <span className="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium bg-slate-100 text-slate-600 border-slate-200" data-testid={`${testId}-status`}>
            For Context
          </span>
        )}
      </div>

      {/* Target track — professional, developmental (never exam-fail styling). */}
      <div className="relative h-2.5 rounded-full bg-slate-100">
        {zoneLeft !== null && !contextOnly && (
          <div
            className={`absolute top-0 h-2.5 rounded-full opacity-25 ${tone.bar}`}
            style={{ left: `${zoneLeft}%`, width: `${zoneWidth}%` }}
            data-testid={`${testId}-zone`}
          />
        )}
        {markerPct !== null && (
          <div
            className="absolute -top-1 h-4.5 w-1.5 rounded-full bg-slate-800 shadow"
            style={{ left: `calc(${markerPct}% - 3px)`, height: '18px' }}
            data-testid={`${testId}-marker`}
          />
        )}
      </div>

      <div className="mt-1.5 flex items-center justify-between text-[11px] text-slate-400">
        <span>
          {rep === 'range' && typeof low === 'number'
            ? `Target zone: ${low}–${high}${unit ? ' ' + unit : ''}`
            : typeof target === 'number'
              ? `Target: ${target}${unit ? ' ' + unit : ''}`
              : bench.categorical_value ? `Target: ${bench.categorical_value}` : '\u00A0'}
        </span>
        {trend && (
          <span className="inline-flex items-center gap-1 text-slate-500" data-testid={`${testId}-trend`}>
            Trend: <strong className="font-medium">{trend.label}</strong>
          </span>
        )}
      </div>

      {comparison.message && !contextOnly && (
        <div className="mt-1.5 text-[11px] text-slate-400">{comparison.message}</div>
      )}
    </div>
  );
};

export default DevelopmentTargetBar;
